import ConfigSidebar from "@/components/config_components/ConfigSidebar/ConfigSidebar.vue";
import OnboardingAiPanel from "@/components/config_components/OnboardingAiPanel/OnboardingAiPanel.vue";
import navbar from "@/components/config_components/Navbar/NavBar.vue";
import { build_onboarding_ai_panel_content } from "@/layout/config_base_layout/onboarding_ai_panel_content";
import { buildOnboardingNameParts } from "@/layout/config_base_layout/onboarding_name_helpers";
import { fetch_form_structure } from "@/services/main_section_service";
import {
    apply_for_onboarding_entity_type,
    create_onboarding_case,
    create_invited_onboarding_case,
    execute_created_onboarding_case,
    extract_onboarding_runtime_ids,
    fetch_all_assigned_onboarding_cases,
    fetch_onboarding_entity_types,
    fetch_onboarding_companies,
    fetch_onboarding_forms_for_entity_type,
    fetch_project_onboarding_entity_types,
  persist_local_onboarding_case_metadata
} from "@/services/onboarding_journey_service";
import { fetch_user_roles } from "@/services/workflow_service";
import {
  build_onboarding_creation_options,
  build_onboarding_metadata_payload,
  find_case_for_entity_type,
  get_entity_type_option_id,
  get_entity_type_option_name
} from "@/views/onboarding_system_views/Applicant/IntakePortal/intake_portal_journey_helpers";

function assign_optional_query_value(query, key, value) {
  const normalized_value = String(value || '').trim();
  if (normalized_value) Reflect.set(query, key, normalized_value);
}

function first_present_onboarding_value(values = []) {
  return values.find((value) => String(value || '').trim()) || '';
}

function build_onboarding_scope_case(application_id, form, selected_project, selected_entity_type, project_display_name) {
  const case_item = {
    application_id,
    name: String(form.entity_name || ''),
    company: project_display_name(selected_project),
    entity_type_id: first_present_onboarding_value([selected_entity_type?.id, form.entity_type_id]),
    journey: first_present_onboarding_value([selected_entity_type?.name]),
    journey_key: first_present_onboarding_value([selected_entity_type?.id])
  };
  return case_item;
}

export default {
  name: 'ConfigsLayout',
  components: {
    ConfigSidebar,
    OnboardingAiPanel,
    navbar
  },
  data: () => ({
    viewport_width: window.innerWidth,
    is_sidebar_hidden: false,
    show_backdrop: false,
    show_mobile_drawer: false,
    show_mobile_ai_drawer: false,
    isLargeScreen: window.innerWidth >= 1200,
    onboarding_modal_visible: false,
    is_loading_onboarding_options: false,
    is_loading_onboarding_companies: false,
    is_loading_onboarding_roles: false,
    is_loading_required_sections: false,
    is_creating_onboarding: false,
    show_onboarding_template_dropdown: false,
    show_onboarding_owner_dropdown: false,
    onboarding_template_search: '',
    onboarding_role_search: '',
    role_dropdown_style: {},
    onboarding_error: '',
    entity_onboarding_modal_visible: false,
    entity_onboarding_error: '',
    is_loading_entity_onboarding_options: false,
    is_loading_entity_onboarding_forms: false,
    is_creating_entity_onboarding: false,
    entity_onboarding_types: [],
    entity_onboarding_forms: [],
    entity_onboarding_form: {
      entity_type_id: ''
    },
    onboarding_entity_types: [],
    onboarding_forms: [],
    onboarding_companies: [],
    onboarding_roles: [],
    onboarding_required_sections: [],
    onboarding_form: {
      entity_name: '',
      entity_profile_id: '',
      entity_type_id: '',
      form_ids: [],
      email: '',
      allow_login: true,
      owner_role_id: '',
      owner_role_ids: [],
      activate_by: ''
    }
  }),
  computed: {
    should_hide_sidebar() {
      const hidden_routes = ['/', '/login'];
      return Boolean(this.$route.meta?.hideSidebar) || hidden_routes.includes(this.$route.path);
    },
    show_desktop_sidebar() {
      if (this.should_hide_sidebar) {
        return false;
      }

      return this.is_onboarding_system_layout
        ? this.viewport_width >= 1200
        : this.viewport_width >= 992;
    },
    show_navbar_overlap() {
      return !this.is_onboarding_system_layout && this.show_desktop_sidebar && !this.is_sidebar_hidden && this.isLargeScreen;
    },
    is_onboarding_system_layout() {
      return Boolean(this.$route.meta?.onboardingSystem);
    },
    selected_onboarding_project() {
      return this.onboarding_companies.find((company) => String(company.id) === String(this.onboarding_form.entity_profile_id)) || null;
    },
    available_onboarding_entity_types() {
      return this.onboarding_entity_types;
    },
    selected_onboarding_templates() {
      const selected_ids = this.get_selected_onboarding_form_ids();
      return this.onboarding_forms.filter((form) => selected_ids.includes(String(form.id)));
    },
    filtered_onboarding_forms() {
      const search = this.onboarding_template_search.trim().toLowerCase();

      if (!search) {
        return this.onboarding_forms;
      }

      return this.onboarding_forms.filter((form) => (
        String(form.name || form.form_name || form.title || '').toLowerCase().includes(search)
      ));
    },
    onboarding_template_label() {
      if (!this.selected_onboarding_templates.length) {
        return 'Select template';
      }

      if (this.selected_onboarding_templates.length === 1) {
        return this.selected_onboarding_templates[0].name;
      }

      return `${this.selected_onboarding_templates.length} templates selected`;
    },
    selected_onboarding_roles() {
      const selected_ids = this.get_selected_onboarding_role_ids();
      return this.onboarding_roles.filter((role) => selected_ids.includes(String(role.id)));
    },
    filtered_onboarding_roles() {
      const search = this.onboarding_role_search.trim().toLowerCase();

      if (!search) {
        return this.onboarding_roles;
      }

      return this.onboarding_roles.filter((role) => (
        this.onboarding_role_display_name(role).toLowerCase().includes(search)
      ));
    },
    onboarding_role_label() {
      if (!this.selected_onboarding_roles.length) {
        return this.is_loading_onboarding_roles ? 'Loading roles...' : 'Select role';
      }

      if (this.selected_onboarding_roles.length === 1) {
        return this.onboarding_role_display_name(this.selected_onboarding_roles[0]);
      }

      return `${this.selected_onboarding_roles.length} roles selected`;
    },
    onboarding_entity_name_label() {
      const selected_entity_type = this.onboarding_entity_types.find((entity_type) => String(entity_type.id) === String(this.onboarding_form.entity_type_id));
      const journey_name = selected_entity_type?.name || 'Onboarding';

      return `${journey_name} name`;
    },
    onboarding_entity_name_placeholder() {
      return `Enter ${this.onboarding_entity_name_label.toLowerCase()}`;
    },
    onboarding_ai_panel_content() {
      return build_onboarding_ai_panel_content(this.$route);
    },
    is_exact_entity_role() {
      return String(this.$store.getters.currentRole || '').trim().toLowerCase() === 'entity';
    }
  },
  watch: {
    '$route.fullPath'() {
      this.close_mobile_sidebar();
      this.close_mobile_ai_sidebar();

      if (this.should_hide_sidebar) this.is_sidebar_hidden = false;
    }
  },
  mounted() {
    this.handle_resize();
    window.addEventListener('resize', this.handle_resize);
    document.addEventListener('mousedown', this.handle_onboarding_dropdown_outside_click);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    document.removeEventListener('mousedown', this.handle_onboarding_dropdown_outside_click);
    document.body.classList.remove('mobile-drawer-open');
  },
  methods: {
    first_defined_onboarding_value(values, fallback = '') {
      const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
      return value === undefined ? fallback : value;
    },
    extract_onboarding_role_sources(role) {
      if (typeof role?.role === 'string') return [role.role];

      return [
        role?.profile_role?.role,
        role?.profile_role?.name,
        role?.profile_role?.role_name,
        role?.role?.role,
        role?.role?.name,
        role?.role?.role_name,
        role?.role_name
      ];
    },
    build_onboarding_role_name(role) {
      return this.extract_onboarding_role_sources(role).find(Boolean) || '';
    },
    build_onboarding_name_parts() {
      const name_parts = buildOnboardingNameParts(this.onboarding_form.entity_name);
      return name_parts;
    },
    get_selected_onboarding_entity_type() {
      return this.onboarding_entity_types.find((entity_type) => String(entity_type.id) === String(this.onboarding_form.entity_type_id));
    },
    get_missing_new_onboarding_fields(selected_form_ids) {
      const missing_fields = [];

      if (!this.onboarding_form.entity_profile_id) {
        missing_fields.push('candidate / company name');
      }
      if (!this.onboarding_form.entity_name) {
        missing_fields.push(this.onboarding_entity_name_label.toLowerCase());
      }
      if (!this.onboarding_form.entity_type_id) {
        missing_fields.push('journey');
      }
      if (!selected_form_ids.length) {
        missing_fields.push('template');
      }

      return missing_fields;
    },
    has_required_new_onboarding_fields(selected_form_ids) {
      return this.get_missing_new_onboarding_fields(selected_form_ids).length === 0;
    },
    validate_new_onboarding_selection(selected_form_ids) {
      if (!this.has_required_new_onboarding_fields(selected_form_ids)) {
        const missing_fields = this.get_missing_new_onboarding_fields(selected_form_ids);
        this.onboarding_error = `Complete the following before creating the onboarding: ${missing_fields.join(', ')}.`;
        return false;
      }

      if (this.onboarding_form.allow_login && !this.onboarding_form.email) {
        this.onboarding_error = 'Enter an email address when login is enabled.';
        return false;
      }

      return true;
    },
    build_new_onboarding_payload({ selected_form_ids, selected_role_ids, selected_project, first_name, last_name }) {
      const payload = {
        entity_name: this.onboarding_form.entity_name,
        entity_type_id: Number(this.onboarding_form.entity_type_id),
        form_ids: selected_form_ids.map((form_id) => Number(form_id)),
        send_credentials: Boolean(this.onboarding_form.allow_login),
        project_id: selected_project.project_id
      };

      if (selected_role_ids.length) payload.owner_role_id = Number(selected_role_ids[0]);
      if (this.onboarding_form.activate_by) payload.activate_by = this.onboarding_form.activate_by;

      if (this.onboarding_form.allow_login) {
        payload.email = this.onboarding_form.email;
        payload.first_name = first_name;
        payload.last_name = last_name;
      }

      return payload;
    },
    validate_onboarding_creation_targets(selected_entity_type, selected_project) {
      if (!selected_entity_type?.id) {
        this.onboarding_error = 'Select a backend journey before creating the onboarding.';
        return false;
      }

      if (!selected_project?.project_id) {
        this.onboarding_error = 'Select a candidate / company name from the backend projects before creating the onboarding.';
        return false;
      }

      return true;
    },
    build_onboarding_creation_context() {
      const selected_form_ids = this.get_selected_onboarding_form_ids();
      const selected_role_ids = this.get_selected_onboarding_role_ids();
      const selected_entity_type = this.get_selected_onboarding_entity_type();
      const selected_project = this.selected_onboarding_project;
      const name_parts = this.build_onboarding_name_parts();
      const creation_context = {
        selected_form_ids: selected_form_ids,
        selected_role_ids: selected_role_ids,
        selected_entity_type: selected_entity_type,
        selected_project: selected_project,
        first_name: name_parts.first_name,
        last_name: name_parts.last_name
      };

      return creation_context;
    },
    async create_onboarding_case_request(context) {
      const create_case = this.onboarding_form.allow_login ? create_invited_onboarding_case : create_onboarding_case;
      const payload = this.build_new_onboarding_payload(context);
      return create_case(payload);
    },
    persist_created_onboarding_case(creation_response, selected_project, selected_entity_type) {
      persist_local_onboarding_case_metadata({
        creation_response,
        onboarding_form: this.onboarding_form,
        selected_project,
        selected_entity_type,
        selected_roles: this.selected_onboarding_roles,
        selected_templates: this.selected_onboarding_templates
      });
    },
    announce_created_onboarding(creation_response, selected_project, selected_entity_type) {
      window.dispatchEvent(new CustomEvent('onboarding-case-created', {
        detail: {
          creation_response,
          execution_response: creation_response?.execution_response || null,
          onboarding_form: { ...this.onboarding_form },
          selected_project,
          selected_entity_type,
          selected_roles: [...this.selected_onboarding_roles],
          selected_templates: [...this.selected_onboarding_templates]
        }
      }));
      window.dispatchEvent(new CustomEvent('onboarding-data-updated'));
    },
    finalize_created_onboarding() {
      this.reset_new_onboarding_form();
      this.onboarding_modal_visible = false;
    },
    get_created_onboarding_application_id(completed_response = {}) {
      const runtime_ids = extract_onboarding_runtime_ids(completed_response);
      const execution_ids = extract_onboarding_runtime_ids(completed_response?.execution_response || {});
      return this.first_defined_onboarding_value([
        runtime_ids.application_id,
        execution_ids.application_id
      ], '');
    },
    build_created_onboarding_form_query(selected_project = {}, selected_entity_type = {}) {
      const query = {
        name: String(this.onboarding_form.entity_name || ''),
        company: this.onboarding_project_display_name(selected_project),
        entity_type_id: String(selected_entity_type?.id || this.onboarding_form.entity_type_id || ''),
        application_form_id: ''
      };
      assign_optional_query_value(query, 'journey', selected_entity_type?.id);
      assign_optional_query_value(query, 'journey_label', selected_entity_type?.name);
      return query;
    },
    dispatch_created_onboarding_scope(completed_response = {}, selected_project = {}, selected_entity_type = {}) {
      const application_id = this.get_created_onboarding_application_id(completed_response);
      if (!application_id) return;

      const case_item = build_onboarding_scope_case(
        application_id,
        this.onboarding_form,
        selected_project,
        selected_entity_type,
        this.onboarding_project_display_name
      );
      window.dispatchEvent(new CustomEvent('onboarding-case-scope-selected', {
        detail: { case_item }
      }));
    },
    async open_created_onboarding_form(completed_response = {}, selected_project = {}, selected_entity_type = {}) {
      const application_id = this.get_created_onboarding_application_id(completed_response);
      if (!application_id) return;

      this.dispatch_created_onboarding_scope(completed_response, selected_project, selected_entity_type);
      await this.$router.push({
        name: 'MyQueueForm',
        params: { applicationId: String(application_id) },
        query: this.build_created_onboarding_form_query(selected_project, selected_entity_type)
      });
    },
    reset_new_onboarding_form() {
      this.onboarding_form = {
        entity_name: '',
        entity_profile_id: '',
        entity_type_id: this.onboarding_entity_types[0]?.id || '',
        form_ids: this.onboarding_forms[0]?.id ? [String(this.onboarding_forms[0].id)] : [],
        email: '',
        allow_login: true,
        owner_role_id: '',
        owner_role_ids: [],
        activate_by: ''
      };
    },
    handle_resize() {
      this.viewport_width = window.innerWidth;
      this.isLargeScreen = this.viewport_width >= 1200;

      if (this.show_desktop_sidebar) {
        this.close_mobile_sidebar();
      }

      if (this.show_onboarding_owner_dropdown) {
        this.position_onboarding_role_dropdown();
      }
    },
    handle_onboarding_dropdown_outside_click(event) {
      if (!this.onboarding_modal_visible) return;

      if (this.should_close_onboarding_dropdown(this.show_onboarding_template_dropdown, this.$refs.onboardingTemplatePicker, event)) {
        this.show_onboarding_template_dropdown = false;
      }

      if (this.should_close_onboarding_dropdown(this.show_onboarding_owner_dropdown, this.$refs.onboardingOwnerPicker, event)) {
        this.show_onboarding_owner_dropdown = false;
      }
    },
    should_close_onboarding_dropdown(is_open, picker, event) {
      return Boolean(is_open && picker && !picker.contains(event.target));
    },
    close_mobile_sidebar() {
      this.show_mobile_drawer = false;
      document.body.classList.remove('mobile-drawer-open');
    },
    close_mobile_ai_sidebar() {
      this.show_mobile_ai_drawer = false;
    },
    toggle_sidebar() {
      this.show_mobile_drawer = !this.show_mobile_drawer;
      document.body.classList.toggle('mobile-drawer-open', this.show_mobile_drawer);
    },
    toggle_ai_sidebar() {
      this.show_mobile_ai_drawer = !this.show_mobile_ai_drawer;
    },
    handle_onboarding_ai_question_selected(question) {
      if (!question) {
        return;
      }

      window.dispatchEvent(new CustomEvent('onboarding-ai-question-selected', {
        detail: {
          question,
          route_name: this.$route.name || ''
        }
      }));
    },
    handle_onboarding_ai_question_submit(question) {
      if (!question) {
        return;
      }

      window.dispatchEvent(new CustomEvent('onboarding-ai-question-submit', {
        detail: {
          question,
          route_name: this.$route.name || ''
        }
      }));
    },
    async load_onboarding_options() {
      this.is_loading_onboarding_options = true;
      this.onboarding_error = '';

      try {
        this.onboarding_entity_types = [];
        this.onboarding_form.entity_type_id = '';
        this.reset_onboarding_template_selection();
        await Promise.all([
          this.load_onboarding_companies(),
          this.load_onboarding_roles()
        ]);
      } catch (error) {
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load onboarding configuration.';
      } finally {
        this.is_loading_onboarding_options = false;
      }
    },
    defer_onboarding_modal_work(callback) {
      this.$nextTick(() => {
        window.setTimeout(callback, 0);
      });
    },
    open_onboarding_modal() {
      if (this.is_exact_entity_role) {
        this.open_entity_onboarding_modal();
        return;
      }

      this.onboarding_modal_visible = true;
      this.onboarding_error = '';

      if (
        !this.onboarding_entity_types.length
        || !this.onboarding_forms.length
        || !this.onboarding_companies.length
        || !this.onboarding_roles.length
      ) {
        this.defer_onboarding_modal_work(() => this.load_onboarding_options());
        return;
      }

      this.defer_onboarding_modal_work(() => this.load_onboarding_required_sections());
    },
    async open_entity_onboarding_modal() {
      this.entity_onboarding_error = '';
      this.entity_onboarding_modal_visible = true;
      if (!this.entity_onboarding_types.length) await this.load_entity_onboarding_types();
      if (this.entity_onboarding_form.entity_type_id) await this.load_entity_onboarding_forms();
    },
    close_entity_onboarding_modal(options = {}) {
      if (this.is_creating_entity_onboarding && !options.force) return;
      this.entity_onboarding_modal_visible = false;
      this.entity_onboarding_error = '';
    },
    async load_entity_onboarding_types() {
      this.is_loading_entity_onboarding_options = true;

      try {
        const entity_types = await fetch_onboarding_entity_types();
        this.entity_onboarding_types = entity_types;
        this.select_initial_entity_onboarding_type(entity_types);
      } catch (error) {
        this.entity_onboarding_types = [];
        this.entity_onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load onboarding types.';
      } finally {
        this.is_loading_entity_onboarding_options = false;
      }
    },
    select_initial_entity_onboarding_type(entity_types = []) {
      if (this.entity_onboarding_form.entity_type_id) return;
      const first_type = entity_types.find((entity_type) => get_entity_type_option_id(entity_type));
      this.entity_onboarding_form.entity_type_id = first_type ? String(get_entity_type_option_id(first_type)) : '';
    },
    async handle_entity_onboarding_type_change() {
      this.entity_onboarding_error = '';
      await this.load_entity_onboarding_forms();
    },
    async load_entity_onboarding_forms() {
      const entity_type_id = Number(this.entity_onboarding_form.entity_type_id || 0);
      if (!entity_type_id) {
        this.entity_onboarding_forms = [];
        return;
      }

      this.is_loading_entity_onboarding_forms = true;
      try {
        this.entity_onboarding_forms = await fetch_onboarding_forms_for_entity_type(entity_type_id);
      } catch {
        this.entity_onboarding_forms = [];
      } finally {
        this.is_loading_entity_onboarding_forms = false;
      }
    },
    selected_entity_onboarding_type() {
      const selected_id = Number(this.entity_onboarding_form.entity_type_id || 0);
      return this.entity_onboarding_types.find((entity_type) => (
        get_entity_type_option_id(entity_type) === selected_id
      )) || null;
    },
    entity_onboarding_type_label(entity_type) {
      return get_entity_type_option_name(entity_type);
    },
    entity_onboarding_type_id(entity_type) {
      return get_entity_type_option_id(entity_type);
    },
    async get_entity_onboarding_cases() {
      const cases = await fetch_all_assigned_onboarding_cases({ _: Date.now() }).catch(() => []);
      return Array.isArray(cases) ? cases : [];
    },
    async get_existing_entity_onboarding_case(entity_type_id) {
      const cases = await this.get_entity_onboarding_cases();
      return find_case_for_entity_type(cases, entity_type_id);
    },
    entity_onboarding_submit_label() {
      return this.is_creating_entity_onboarding ? 'Creating...' : 'Create onboarding';
    },
    get_selected_entity_onboarding_context() {
      const selected_entity_type = this.selected_entity_onboarding_type();
      const context = {
        selected_entity_type,
        entity_type_id: get_entity_type_option_id(selected_entity_type)
      };
      return context;
    },
    has_entity_onboarding_type(entity_type_id) {
      return Boolean(entity_type_id);
    },
    set_missing_entity_onboarding_type_error() {
      this.entity_onboarding_error = 'Select the onboarding type you want to start.';
    },
    validate_entity_onboarding_type(entity_type_id) {
      if (this.has_entity_onboarding_type(entity_type_id)) return true;
      this.set_missing_entity_onboarding_type_error();
      return false;
    },
    set_entity_onboarding_create_state(is_creating) {
      this.is_creating_entity_onboarding = is_creating;
      if (is_creating) this.entity_onboarding_error = '';
    },
    async submit_entity_onboarding_case() {
      const context = this.get_selected_entity_onboarding_context();
      const entity_type_id = context.entity_type_id;
      if (!this.validate_entity_onboarding_type(entity_type_id)) return;

      await this.run_entity_onboarding_submission(entity_type_id, context.selected_entity_type);
    },
    async run_entity_onboarding_submission(entity_type_id, selected_entity_type) {
      this.set_entity_onboarding_create_state(true);

      try {
        await this.open_or_create_entity_onboarding(entity_type_id, selected_entity_type);
      } catch (error) {
        this.entity_onboarding_error = error?.response?.data?.message || error?.message || 'Unable to create onboarding.';
      } finally {
        this.set_entity_onboarding_create_state(false);
      }
    },
    async open_or_create_entity_onboarding(entity_type_id, selected_entity_type) {
      const existing_case = await this.get_existing_entity_onboarding_case(entity_type_id);
      const case_item = existing_case || await this.create_entity_onboarding_case(entity_type_id, selected_entity_type);
      if (!case_item?.application_id) throw new Error('The onboarding was created, but the assigned form is not available yet.');
      this.close_entity_onboarding_modal({ force: true });
      this.open_entity_onboarding_form(case_item);
    },
    async create_entity_onboarding_case(entity_type_id, selected_entity_type) {
      const creation_response = await apply_for_onboarding_entity_type(build_onboarding_creation_options(entity_type_id));
      persist_local_onboarding_case_metadata(build_onboarding_metadata_payload({ creation_response, selected_entity_type }));
      await execute_created_onboarding_case({ creation_response, entity_type_id });
      window.dispatchEvent(new CustomEvent('onboarding-data-updated'));
      return this.get_existing_entity_onboarding_case(entity_type_id);
    },
    dispatch_entity_onboarding_scope(case_item) {
      const scope_event = new CustomEvent('onboarding-case-scope-selected', {
        detail: { case_item }
      });
      window.dispatchEvent(scope_event);
    },
    get_case_item_text(case_item, key) {
      return String(Reflect.get(case_item, key) || '');
    },
    get_case_item_entity_type_id(case_item) {
      const direct_entity_type_id = Reflect.get(case_item, 'entity_type_id');
      const threshold_entity_type_id = Reflect.get(case_item, 'threshold_entity_type_id_used');
      return String(direct_entity_type_id || threshold_entity_type_id || '');
    },
    get_entity_onboarding_form_query(case_item = {}) {
      const query = {
        name: this.get_case_item_text(case_item, 'name'),
        company: this.get_case_item_text(case_item, 'company'),
        entity_type_id: this.get_case_item_entity_type_id(case_item),
        journey: this.get_case_item_text(case_item, 'journey'),
        application_form_id: ''
      };
      return query;
    },
    open_entity_onboarding_form(case_item = {}) {
      if (!case_item?.application_id) return;
      this.dispatch_entity_onboarding_scope(case_item);
      this.$router.push({
        name: 'MyQueueForm',
        params: { applicationId: String(case_item.application_id) },
        query: this.get_entity_onboarding_form_query(case_item)
      });
    },
    async load_onboarding_templates_for_journey() {
      const selected_entity_type = this.onboarding_entity_types.find((entity_type) => String(entity_type.id) === String(this.onboarding_form.entity_type_id));

      if (!this.onboarding_form.entity_type_id) {
        this.reset_onboarding_template_selection();
        return;
      }

      try {
        const forms = await this.get_onboarding_forms_for_selected_journey(selected_entity_type);
        this.onboarding_forms = forms;
        this.sync_selected_onboarding_forms(forms);
        await this.load_onboarding_required_sections();
      } catch (error) {
        this.reset_onboarding_template_selection();
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load templates for the selected journey.';
      }
    },
    reset_onboarding_template_selection() {
      this.onboarding_forms = [];
      this.onboarding_form.form_ids = [];
      this.onboarding_required_sections = [];
    },
    async get_onboarding_forms_for_selected_journey(selected_entity_type) {
      if (!selected_entity_type?.id) {
        return [];
      }

      return fetch_onboarding_forms_for_entity_type(this.onboarding_form.entity_type_id, {
        project_id: this.selected_onboarding_project?.project_id,
        sub_entity_type_id: selected_entity_type?.sub_entity_type_id
      });
    },
    sync_selected_onboarding_forms(forms) {
      const valid_form_ids = forms.map((form) => String(form.id));
      const selected_form_ids = this.get_selected_onboarding_form_ids()
        .filter((form_id) => valid_form_ids.includes(form_id));

      this.onboarding_form.form_ids = selected_form_ids.length
        ? selected_form_ids
        : forms[0]?.id ? [String(forms[0].id)] : [];
    },
    async load_onboarding_companies() {
      this.is_loading_onboarding_companies = true;

      try {
        this.onboarding_companies = await fetch_onboarding_companies();

        if (!this.onboarding_companies.some((company) => String(company.id) === String(this.onboarding_form.entity_profile_id))) {
          this.onboarding_form.entity_profile_id = '';
          this.onboarding_form.entity_name = '';
        }
      } catch (error) {
        this.onboarding_companies = [];
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load backend projects for candidate / company names.';
      } finally {
        this.is_loading_onboarding_companies = false;
      }
    },
    extract_response_items(response) {
      const parsed = response?.data ?? response ?? {};
      const candidates = [
        parsed?.data?.results,
        parsed?.data?.roles,
        parsed?.data?.items,
        parsed?.data?.data,
        parsed?.data,
        parsed?.roles,
        parsed?.items,
        parsed?.results,
        parsed
      ];

      return candidates.find((candidate) => Array.isArray(candidate)) || [];
    },
    normalize_onboarding_role(role) {
      const raw_role = role?.profile_role || (role?.role && typeof role.role === 'object' ? role.role : role);
      const role_name = this.build_onboarding_role_name(role);
      const normalized_role = {
        ...role,
        id: raw_role?.id || raw_role?.role_id || role?.role_id || role?.id,
        name: role_name || 'Role'
      };

      return normalized_role;
    },
    async load_onboarding_roles() {
      this.is_loading_onboarding_roles = true;

      try {
        const response = await fetch_user_roles();
        this.onboarding_roles = this.extract_response_items(response)
          .map((role) => this.normalize_onboarding_role(role))
          .filter((role) => role.id && role.name);

        const valid_role_ids = this.onboarding_roles.map((role) => String(role.id));
        const selected_role_ids = this.get_selected_onboarding_role_ids()
          .filter((role_id) => valid_role_ids.includes(role_id));

        this.onboarding_form.owner_role_ids = selected_role_ids;
        this.onboarding_form.owner_role_id = selected_role_ids[0] || '';
      } catch (error) {
        this.onboarding_roles = [];
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load roles from the backend.';
      } finally {
        this.is_loading_onboarding_roles = false;
      }
    },
    on_onboarding_company_change() {
      this.load_onboarding_entity_types_for_company();
    },
    onboarding_project_display_name(project) {
      return project?.name || project?.project_name || project?.title || `Project ${project?.id || ''}`;
    },
    get_selected_onboarding_form_ids() {
      return Array.isArray(this.onboarding_form.form_ids)
        ? this.onboarding_form.form_ids.map((form_id) => String(form_id)).filter(Boolean)
        : [];
    },
    is_onboarding_template_selected(form_id) {
      return this.get_selected_onboarding_form_ids().includes(String(form_id));
    },
    onboarding_role_display_name(role) {
      return role?.name || role?.role || role?.role_name || `Role ${role?.id || ''}`;
    },
    get_selected_onboarding_role_ids() {
      if (Array.isArray(this.onboarding_form.owner_role_ids)) {
        return this.onboarding_form.owner_role_ids.map((role_id) => String(role_id)).filter(Boolean);
      }

      return this.onboarding_form.owner_role_id ? [String(this.onboarding_form.owner_role_id)] : [];
    },
    is_onboarding_role_selected(role_id) {
      return this.get_selected_onboarding_role_ids().includes(String(role_id));
    },
    toggle_onboarding_template_dropdown() {
      this.show_onboarding_template_dropdown = !this.show_onboarding_template_dropdown;
      this.show_onboarding_owner_dropdown = false;
    },
    toggle_onboarding_owner_dropdown() {
      if (this.is_loading_onboarding_roles) return;

      this.show_onboarding_owner_dropdown = !this.show_onboarding_owner_dropdown;
      this.show_onboarding_template_dropdown = false;

      if (this.show_onboarding_owner_dropdown) this.$nextTick(this.position_onboarding_role_dropdown);
    },
    position_onboarding_role_dropdown() {
      const button = this.$refs.onboardingOwnerButton;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const viewport_padding = 12;
      const available_height = Math.max(150, window.innerHeight - rect.bottom - viewport_padding);
      const dropdown_height = Math.min(320, available_height);

      this.role_dropdown_style = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 4}px`,
        width: `${rect.width}px`,
        maxHeight: `${dropdown_height}px`
      };
    },
    toggle_onboarding_template(form) {
      const form_id = String(form?.id || '');
      if (!form_id) return;

      const selected_ids = new Set(this.get_selected_onboarding_form_ids());

      if (selected_ids.has(form_id)) {
        selected_ids.delete(form_id);
      } else {
        selected_ids.add(form_id);
      }

      this.onboarding_form.form_ids = Array.from(selected_ids);
      this.load_onboarding_required_sections();
    },
    toggle_onboarding_role(role) {
      const role_id = String(role?.id || '');
      if (!role_id) return;

      const selected_ids = new Set(this.get_selected_onboarding_role_ids());

      if (selected_ids.has(role_id)) {
        selected_ids.delete(role_id);
      } else {
        selected_ids.add(role_id);
      }

      const next_ids = Array.from(selected_ids);
      this.onboarding_form.owner_role_ids = next_ids;
      this.onboarding_form.owner_role_id = next_ids[0] || '';
    },
    async on_onboarding_journey_change(options = {}) {
      const preserve_company = options?.preserve_company !== false;

      this.onboarding_form.form_ids = [];
      this.show_onboarding_template_dropdown = false;
      this.onboarding_template_search = '';
      if (!preserve_company) this.reset_onboarding_company_selection();
      await Promise.all([
        this.load_onboarding_templates_for_journey(),
        preserve_company ? Promise.resolve() : this.load_onboarding_companies()
      ]);
    },
    reset_onboarding_company_selection() {
      this.onboarding_form.entity_profile_id = '';
      this.onboarding_form.entity_name = '';
      this.onboarding_form.email = '';
    },
    async load_onboarding_entity_types_for_company() {
      const selected_project = this.selected_onboarding_project;

      this.onboarding_entity_types = [];
      this.onboarding_form.entity_type_id = '';
      this.reset_onboarding_template_selection();

      if (!selected_project?.project_id) {
        return;
      }

      const entity_types = await fetch_project_onboarding_entity_types(selected_project.project_id).catch((error) => {
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load entity types for the selected company.';
        return null;
      });
      if (!entity_types) {
        return;
      }

      this.onboarding_entity_types = entity_types;
      this.onboarding_form.entity_type_id = entity_types[0]?.id ? String(entity_types[0].id) : '';
      if (this.onboarding_form.entity_type_id) await this.load_onboarding_templates_for_journey();
    },
    close_onboarding_modal() {
      if (this.is_creating_onboarding) return;

      this.onboarding_modal_visible = false;
      this.show_onboarding_template_dropdown = false;
      this.show_onboarding_owner_dropdown = false;
      this.onboarding_template_search = '';
      this.onboarding_role_search = '';
    },
    async submit_new_onboarding() {
      const creation_context = this.build_onboarding_creation_context();
      const selected_form_ids = creation_context.selected_form_ids;
      const selected_project = creation_context.selected_project;
      const selected_entity_type = creation_context.selected_entity_type;

      if (!this.validate_new_onboarding_selection(selected_form_ids)) return;

      this.is_creating_onboarding = true;
      this.onboarding_error = '';

      try {
        if (!this.validate_onboarding_creation_targets(selected_entity_type, selected_project)) return;

        const creation_response = await this.create_onboarding_case_request(creation_context);
        const execution_response = await execute_created_onboarding_case({
          creation_response,
          entity_type_id: selected_entity_type.id,
          project_id: selected_project.project_id,
          sub_entity_type_id: selected_entity_type.sub_entity_type_id,
          form_ids: selected_form_ids
        });
        const completed_response = Object.assign({}, creation_response || {}, { execution_response });
        this.persist_created_onboarding_case(completed_response, selected_project, selected_entity_type);
        this.announce_created_onboarding(completed_response, selected_project, selected_entity_type);
        this.finalize_created_onboarding();
        await this.open_created_onboarding_form(completed_response, selected_project, selected_entity_type);
      } catch (error) {
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to create the onboarding.';
      } finally {
        this.is_creating_onboarding = false;
      }
    },
    build_required_section(section, fallback_id) {
      const label = this.first_defined_onboarding_value([
        section?.main_section_name,
        section?.section_name,
        section?.name,
        section?.label
      ], '');
      const trimmed_label = String(label || '').trim();

      if (!trimmed_label) return null;

      const section_id = this.first_defined_onboarding_value([
        section?.main_section_id,
        section?.section_id,
        section?.id
      ], fallback_id);
      const required_section = { id: section_id, label: trimmed_label };

      return required_section;
    },
    flatten_onboarding_required_sections(form_structure) {
      const main_sections = Array.isArray(form_structure) ? form_structure : [];
      const sections = [];

      main_sections.forEach((section, section_index) => {
        const record = this.build_required_section(section, `main-section-${section_index}`);
        if (record) sections.push(record);
      });

      return this.dedupe_onboarding_required_sections(sections);
    },
    async load_onboarding_required_sections() {
      const selected_form_ids = this.get_selected_onboarding_form_ids();

      if (!this.onboarding_form.entity_type_id && !selected_form_ids.length) {
        this.onboarding_required_sections = [];
        return;
      }

      this.is_loading_required_sections = true;
      this.onboarding_error = '';

      try {
        if (!selected_form_ids.length) {
          this.onboarding_required_sections = [];
          return;
        }

        const form_structure_results = await Promise.all(
          selected_form_ids.map((form_id) => fetch_form_structure(form_id).catch(() => []))
        );
        const form_structure_sections = form_structure_results.flatMap((form_structure) =>
          this.flatten_onboarding_required_sections(form_structure)
        );

        this.onboarding_required_sections = this.dedupe_onboarding_required_sections(form_structure_sections);
      } catch (error) {
        this.onboarding_required_sections = [];
        this.onboarding_error = error?.response?.data?.message || error?.message || 'Unable to load template sections.';
      } finally {
        this.is_loading_required_sections = false;
      }
    },
    dedupe_onboarding_required_sections(sections) {
      const seen = new Set();
      return sections.filter((section) => {
        const key = String(section.id || section.label);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
  }
}
